import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from './utils';

/**
 * Button — styled per Pelipost UX/UI Style Guide §6.
 *
 *   default / primary   Orange bg, white text, 16px radius, 600 weight
 *                       Used for: Log In, Create Account, Place Order, Search
 *   outline             Transparent bg, 1.5px CTA border, CTA text
 *                       Used for: Sign Up, + Add New Contact
 *   ghost               Transparent bg, CTA text
 *                       Used for: "Already have an account? Log In", "No thanks"
 *   destructive         Same visual as default; destructive intent is signalled
 *                       by context (modal copy). Style guide §6 says modals use
 *                       orange for primary action.
 *   cancel              Bordered, 1px border, text color — used as modal cancel.
 *   link                Underlined CTA text — inline links.
 *
 * Size defaults to `md`. Use `lg` for hero CTAs — min-height 56px per guide.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand rounded-[16px] active:opacity-70',
  {
    variants: {
      variant: {
        default: 'bg-cta text-white hover:bg-cta-hover',
        outline:
          'bg-transparent border-[1.5px] border-cta text-cta hover:bg-cta-light',
        secondary: 'bg-cta-light text-cta hover:bg-cta-light/80',
        ghost: 'bg-transparent text-cta hover:bg-cta-light',
        destructive: 'bg-cta text-white hover:bg-cta-hover',
        cancel:
          'bg-transparent border border-border text-text hover:bg-background',
        link: 'text-cta underline-offset-4 hover:underline rounded-none',
      },
      size: {
        sm: 'min-h-10 px-4 py-2 text-sm',
        md: 'min-h-12 px-5 py-3 text-base',
        lg: 'min-h-14 px-6 py-4 text-lg',
        icon: 'size-11',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      fullWidth: false,
    },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(({ className, variant, size, fullWidth, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, fullWidth, className }))}
      {...props}
    />
  );
});

Button.displayName = 'Button';

export { Button, buttonVariants };
